/**
 * Taxonomy manager (admin) — dimensions and their controlled values.
 *
 * Collapsed by default under the repositories list. Built-in dimensions
 * (line of business, department, business application) can be relabelled or
 * archived, never deleted; custom dimensions can be added. Values archive
 * rather than delete, so existing repo tags and project rules stay resolvable.
 */

import { useState } from 'react';
import { Archive, ArchiveRestore, ChevronDown, ChevronRight, Plus, Tags } from 'lucide-react';
import { projectsApi, type TaxonomyDimension } from '@/services/projectsApi';
import { dimStyle } from '@/components/settings/TagChips';
import { cn } from '@/lib/utils';

export function TaxonomyManager({
  dimensions,
  onChanged,
}: {
  dimensions: TaxonomyDimension[];
  onChanged: () => Promise<void>;
}) {
  // Open by default: since workspace-organization Phase B this renders on its
  // own Settings ▸ Taxonomy page rather than at the foot of the GitHub page.
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newDim, setNewDim] = useState('');
  const [addingValueTo, setAddingValueTo] = useState<string | null>(null);
  const [newValue, setNewValue] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Taxonomy update failed');
    }
  }

  return (
    <section className="mt-10">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Tags className="w-4 h-4 text-slate-500" />
        Business taxonomy
      </button>
      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 mb-4">
        Dimensions and values used to tag repositories and drive project membership rules.
      </p>
      {open && (
        <div className="space-y-4">
          {error && (
            <p className="p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
              {error}
            </p>
          )}
          {dimensions
            .filter((d) => showArchived || !d.archivedAt)
            .map((dim) => (
              <div
                key={dim.dimKey}
                className={cn(
                  'rounded-lg border border-slate-200 dark:border-slate-800 p-3',
                  dim.archivedAt && 'opacity-60',
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium',
                        dimStyle(dimensions, dim.dimKey),
                      )}
                    >
                      {dim.label}
                    </span>
                    <span className="text-xs text-slate-400">
                      {dim.dimKey}
                      {dim.builtin && ' · built-in'}
                      {dim.archivedAt && ' · archived'}
                    </span>
                  </div>
                  <button
                    type="button"
                    title={dim.archivedAt ? 'Restore dimension' : 'Archive dimension'}
                    onClick={() =>
                      run(() => projectsApi.updateDimension(dim.dimKey, { archived: !dim.archivedAt }))
                    }
                    className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    {dim.archivedAt ? (
                      <ArchiveRestore className="w-3.5 h-3.5" />
                    ) : (
                      <Archive className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {dim.values
                    .filter((v) => showArchived || !v.archivedAt)
                    .map((v) => (
                      <span
                        key={v.valueSlug}
                        className={cn(
                          'group inline-flex items-center gap-1 rounded border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-xs text-slate-700 dark:text-slate-300',
                          v.archivedAt && 'line-through opacity-60',
                        )}
                      >
                        {v.label}
                        <button
                          type="button"
                          title={v.archivedAt ? 'Restore value' : 'Archive value'}
                          onClick={() =>
                            run(() =>
                              projectsApi.updateValue(dim.dimKey, v.valueSlug, {
                                archived: !v.archivedAt,
                              }),
                            )
                          }
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600"
                        >
                          {v.archivedAt ? (
                            <ArchiveRestore className="w-3 h-3" />
                          ) : (
                            <Archive className="w-3 h-3" />
                          )}
                        </button>
                      </span>
                    ))}
                  {!dim.archivedAt &&
                    (addingValueTo === dim.dimKey ? (
                      <span className="inline-flex items-center gap-1">
                        <input
                          autoFocus
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newValue.trim()) {
                              run(() =>
                                projectsApi.createValue(dim.dimKey, {
                                  valueSlug: slugify(newValue),
                                  label: newValue.trim(),
                                }),
                              ).then(() => {
                                setNewValue('');
                                setAddingValueTo(null);
                              });
                            }
                            if (e.key === 'Escape') setAddingValueTo(null);
                          }}
                          placeholder="Value name…"
                          className="w-36 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-xs"
                        />
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setAddingValueTo(dim.dimKey);
                          setNewValue('');
                        }}
                        className="inline-flex items-center gap-0.5 rounded border border-dashed border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs text-slate-400 hover:text-slate-600 hover:border-slate-400"
                      >
                        <Plus className="w-3 h-3" /> value
                      </button>
                    ))}
                </div>
              </div>
            ))}
          <div className="flex items-center gap-3">
            <input
              value={newDim}
              onChange={(e) => setNewDim(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDim.trim()) {
                  run(() =>
                    projectsApi.createDimension({
                      dimKey: slugify(newDim),
                      label: newDim.trim(),
                    }),
                  ).then(() => setNewDim(''));
                }
              }}
              placeholder="New dimension (e.g. Region)…"
              className="w-56 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
            />
            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          </div>
        </div>
      )}
    </section>
  );
}
